/**
 * KAN-663 / KAN-871: выбор нашего Telegram-аккаунта, с которого уходит ручное
 * сообщение клиенту. Общий для окна переписки кабинета и карточки клиента
 * приложения менеджера:
 *   - варианты: наши аккаунты из ленты (последний первым), чужие личные
 *     аккаунты менеджеров не предлагаются, свой личный (/auth/me
 *     personal_telegram_account) - "От себя (@username)";
 *   - выбор есть только при нескольких вариантах, по умолчанию первый;
 *     единственный личный вариант уходит явно; рядом с личным всегда
 *     вариант компании (из ленты или карточки клиента), если он известен;
 *   - account: подпись "С аккаунта", совпадает с реальной отправкой;
 *   - latestAccount: последний аккаунт ленты, с которого текущий пользователь
 *     может писать.
 */
import { useEffect, useMemo, useState } from 'react';
import { useCachedCurrentUser } from '@/hooks/useCurrentUser';
import {
  isPersonalSenderAccount,
  latestSenderAccount,
  manualSendChannel,
  senderAccountOptions,
  type SenderAccountViewer,
} from '@/lib/senderAccount';
import type { EstimateMessage, MessageSenderAccount } from '@/types/estimate';

interface Options {
  /** false: вариантов нет (например, переписка без клиента). */
  enabled?: boolean;
  /** Смена ключа (другой клиент) сбрасывает выбор. */
  resetKey?: string | null;
  /** Аккаунт из карточки клиента: подпись без ленты и вариант компании рядом с личным. */
  fallbackAccount?: MessageSenderAccount | null;
}

export function useSenderAccountChoice(
  messages: EstimateMessage[] | undefined,
  { enabled = true, resetKey = null, fallbackAccount = null }: Options = {}
) {
  const user = useCachedCurrentUser();
  const managerId = user?.manager_id ?? null;
  const personalAccount = user?.personal_telegram_account ?? null;
  const viewer = useMemo<SenderAccountViewer>(
    () => ({ managerId, personalAccount }),
    [managerId, personalAccount]
  );

  const options = useMemo(
    () => (enabled ? senderAccountOptions(messages, viewer, fallbackAccount) : []),
    [enabled, messages, viewer, fallbackAccount]
  );
  const latestAccount = useMemo(() => latestSenderAccount(messages, viewer), [messages, viewer]);

  // KAN-931: the choice is the option key (channel + account id).
  const [chosenKey, setChosenKey] = useState<string | null>(null);
  useEffect(() => setChosenKey(null), [resetKey]);

  // Выбор существует только при нескольких вариантах; тогда запрос всегда
  // называет аккаунт (выбранный или первый), backend не угадывает.
  // M1: единственный вариант - личный аккаунт: его account_id тоже уходит
  // явно, иначе backend выберет аккаунт компании (личный в автоподборе
  // исключён), а подпись покажет личный.
  const sendOption =
    options.length > 1
      ? (options.find((o) => o.key === chosenKey) ?? options[0])
      : options.length === 1 && isPersonalSenderAccount(options[0].account)
        ? options[0]
        : null;
  // Подпись "С аккаунта" = аккаунт, с которого уйдёт сообщение.
  const account = sendOption ? sendOption.account : (latestAccount ?? fallbackAccount ?? null);
  // KAN-931: channel of the send, also with one (company) option or no
  // options at all; useManualChat sends it to the backend unless Telegram.
  const sendChannel = manualSendChannel(sendOption, options, account, messages);

  return {
    options,
    sendOption,
    sendChannel,
    chooseAccount: setChosenKey,
    latestAccount,
    account,
  };
}
